import { first, tap, mergeMap, takeUntil, delay } from 'rxjs/operators';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Data, Params, Router, UrlSegment } from '@angular/router';
import { EventService, ItemService, PermissionService, StreamService, TenantService, UserService } from '../services'
import { BehaviorSubject, forkJoin, Subject } from 'rxjs';
import * as _ from 'lodash';
import { FormControl } from '@angular/forms';
import { ItemContextModel } from '../model/itemContext.model';

@Component({
    styles: [`
        .title {
            font-size: 20px;
        }
        
        .itemsPanel {
            overflow-y: auto;
            height: calc(100vh - 270px);
        }

        .adminItemsPanel {
            overflow-y: auto;
            /* was 240 */
            height: calc(100vh - 315px); 
        }

        .shortItemsPanel {
            overflow-y: auto;
            height: calc(100vh - 265px);
        }

        .warning-panel {
            background-color: #D7D7D7; 
            padding: 15px;
        }

        .warning-icon {
            padding-right: 15px; 
            padding-bottom: 10px; 
            margin-top: -5px;
        }
    `],
    template: `
        <div *ngIf="noTouching && !showItem" fxLayout="column" fxLayoutAlign="center center" style="height:100%">
            <spinner></spinner>
        </div>
        <div fxLayout="column" *ngIf="!noTouching && !showItem">
            <topic-summary [topic]="topic"
                           [sortField]="request.sortField"
                           (sorted)="sorted($event)"  
                           style="padding-bottom: 2px;"></topic-summary>
            <mat-toolbar *ngIf="canEditTopic" style="height: 40px;">
                <button mat-button [routerLink]="['post']">+ Item</button>
                <span fxFlex></span>
                <label style="padding-right: 10px; font-size: 14px;">Display:</label>
                <mat-select placeholder="Show" [formControl]="show" style="max-width: 110px; padding-right: 15px; font-size: 14px;">
                    <mat-option value="all">All</mat-option>
                    <mat-option value="displayed">Displayed</mat-option>
                    <mat-option value="hidden">Hidden</mat-option>
                </mat-select>
            </mat-toolbar>
            <mat-card *ngIf="isOversubscribedProduct() && !this.topic.userHasStreamSubscriberRole">
                <mat-card-content class="warning-panel">
                    <span *ngIf="!tenant.settings.productOversubscribedMessage">
                        This source for {{topic.title}} requires you to register to view content and is currently over subscribed. Please contact your site administrator.
                    </span>
                    <span *ngIf="tenant.settings.productOversubscribedMessage">{{tenant.settings.productOversubscribedMessage}}</span>
                </mat-card-content>
            </mat-card>       
            <mat-card *ngIf="!isProductAndRestricted() && !topic?.openForReading && !this.topic.userHasStreamSubscriberRole && !userIsStreamManager()">
                <mat-card-content class="warning-panel">
                    <div fxLayout="row">
                        <mat-icon class="warning-icon">priority_high</mat-icon><h3 style="margin-top:0px;">Permission Required</h3>
                    </div>
                    You do not have permission to read the content of this topic, below is a preview. Please contact your site administrator.
                </mat-card-content>
            </mat-card>
            <div class="itemsPanel"
                 [ngClass]="{ 
                     'adminItemsPanel': canEditTopic, 
                     'itemsPanel': !canEditTopic && !isShortItems,
                     'shortItemsPanel': !canEditTopic && isShortItems
                  }" 
                 infiniteScroll 
                 [infiniteScrollThrottle]="300"
                 [infiniteScrollDistance]="1"
                 [scrollWindow]="false"
                 (scrolled)="page()">
                <span *ngFor="let item of items">
                    <item-summary [item]="item" 
                                  [topic]="topic" 
                                  [isAdmin]="canEditTopic"
                                  [subscription]="subscriptions[topic.id]"></item-summary>
                </span>
                <div *ngIf="mostlyNoTouching && !showItem" fxLayout="row" fxLayoutAlign="center center" style="padding-top: 20px;">
                    <spinner></spinner>
                </div>
            </div>
        </div>
        <router-outlet></router-outlet>
    `})
export class TopicPaneComponent implements OnDestroy, OnInit {
    private cleanupTime = new Subject<void>();
    digest: any;
    tenant: any;
    topicId: string;
    topic: any = {};
    contextTopicSubject = new BehaviorSubject(this.topic);
    showItem: boolean = false;
    items: any = [];
    noTouching: boolean = true;
    mostlyNoTouching: boolean = true;
    request: { 
        page: number, 
        rows?: number, 
        includeReadItems?: boolean; 
        displayedOnly?: boolean; 
        hiddenOnly?: boolean; 
        streamId?: any; 
        sortField?: string; 
        search?: string;
        includeFullDescription: boolean 
    } = { page: -1, includeFullDescription: true };
    subscriptions: any;
    canEditTopic: boolean;
    show = new FormControl('all');
    isShortItems: any;

    constructor(private router: Router,
                private route: ActivatedRoute,
                private eventService: EventService,
                private itemService: ItemService,
                private permissionService: PermissionService,
                private streamService: StreamService,
                private tenantService: TenantService,
                private userService: UserService){
        this.show.valueChanges.pipe(takeUntil(this.cleanupTime)).subscribe(mode => {
            this.request.displayedOnly =  mode === 'displayed';
            this.request.hiddenOnly = mode === 'hidden';
            this.noTouching = true;
            this.request.page = -1;
            this.items = [];
            this.fetchItems();
        });
    }

    ngOnInit() {
        this.noTouching = true;
        this.showItem = !!this.route.firstChild;

        this.tenantService.getUnauthTenantInfo().pipe(first())
        .subscribe(tenant => {
            this.tenant = tenant;
            this.route.data.pipe(takeUntil(this.cleanupTime)).subscribe((data: Data) => {
                this.isShortItems = data.isShortItems;
            });
            this.route.params.pipe(takeUntil(this.cleanupTime)).subscribe((params: Params) => {
                this.topicId = params.topicId;
                this.items = [];
                this.request.streamId = this.topicId;
                this.request.page = -1;
                this.mostlyNoTouching = true;
                this.show.setValue('all', { emitEvent: false });
                this.streamService.getStream(this.topicId)
                .subscribe(stream => {
                    this.topic = stream;
                    this.canEditTopic = this.topic &&
                               this.topic.tenantId && 
                               this.topic.tenantId === this.tenant.id &&
                               this.topic.type === 'COLLECTION' &&  
                               this.permissionService.currentUserCanManageStream(this.topic);
                    this.contextTopicSubject.next(this.topic);
                    const source = this.route.snapshot.queryParams.source;
                    this.eventService.postEvent('topic_browsed', { topic: this.eventService.formatStream(this.topic), source });
                    if (this.route.snapshot.queryParams.subscribe && !this.topic.userIsFollowing){
                        this.streamService.setSubscription(this.topic.id, this.userService.getCurrentUser().id, true).pipe(first())
                        .subscribe(() => {
                            this.topic.followersCount++;
                            this.topic.userIsFollowing = true;
                            this.streamService.streamUpdated(this.topic);
                        });
                    }
                    this.request.displayedOnly = !this.canEditTopic;
                    this.request.hiddenOnly = false;
                    this.fetchItems();
                });
            });

            this.itemService.itemClosing.pipe(takeUntil(this.cleanupTime)).subscribe(() => this.router.navigateByUrl(this.itemService.childClearedPath()));
            this.itemService.itemContextChanged
            .pipe(
                delay(2000),
                takeUntil(this.cleanupTime)
            ).subscribe((change) => {
                if (!change?.topicId || change?.topicId === this.topic.id) {
                    this.sorted(this.request.sortField);
                }
            });
            this.route.url.pipe(takeUntil(this.cleanupTime)).subscribe((segs: UrlSegment[]) => {
                if (segs.length) {
                    if (segs[0].path === 'post') {
                        this.itemService.focusedItem.next(undefined);
                        this.itemService.focusedItemEdit.next({ topicId: this.topic.id });
                    } else {
                        this.itemService.focusedItemEdit.next(undefined);
                        const itemId = segs[1].path;
                        let item = this.items.find(item => item.id === itemId);
                        const isSource = false; 
                        const curating = false;
                        if (!item) {
                            forkJoin([
                                this.streamService.getStream(this.route.snapshot.params.topicId),
                                this.itemService.getItem(itemId)
                            ])
                            .subscribe(([topic, { item }]) => {
                                item.visible = true;
                                this.itemService.focusedItem.next(new ItemContextModel(item, undefined, undefined, topic, isSource, curating));
                            });
                        } else {
                            this.itemService.focusedItem.next(new ItemContextModel(item, undefined, undefined, this.topic, isSource, curating));
                        }
                    }
                } else {
                    this.itemService.focusedItem.next(undefined);
                    this.itemService.focusedItemEdit.next(undefined);
                }
            });
        });
    }
    
    userIsStreamManager = () => this.permissionService.currentUserHasPermission({ streamId: this.topicId , action: 'MANAGE_STREAM'})

    subscribeToProdSource = () => 
        this.streamService.addStreamRole(this.userService.getCurrentUser().id, this.topic.id, 'STREAM_SUBSCRIBER')
        .pipe(
            mergeMap(() => this.userService.refetchUser()),
            first()
        ).subscribe(results => this.topic.userHasStreamSubscriberRole = true);

    isProductAndRestricted = () =>  this.topic && this.topic.type === 'PRODUCT_SOURCE' && this.tenant.settings.limitProductSources === 'true' && !this.topic.userHasStreamSubscriberRole;
    
    isOversubscribedProduct = () => this.topic && this.topic.type === 'PRODUCT_SOURCE' && this.topic.streamSubscribersCount >= this.topic.maxStreamSubscribers

    retrievePage = () => {
        this.subscriptions = _.keyBy((this.userService.getCurrentUser() || {}).subscriptions, 'streamId');
        this.request.page++;
        return this.itemService.searchItemsInStream(this.request)
        .pipe(
            tap(itemResults => {
                itemResults.content.forEach(item => {
                    if (this.topic.type !== 'COLLECTION') {
                        item.visible = true;
                    }
                    this.items.push(item);
                });
                this.mostlyNoTouching = false;
            })
        );
    };

    sorted(sortField): void {
        this.request.sortField = sortField;
        this.mostlyNoTouching = true;
        this.items = [];
        this.request.page = -1;
        this.fetchItems();
    }

    page() {
        this.mostlyNoTouching = true;
        this.fetchItems();
    }

    fetchItems = () => this.retrievePage().subscribe(() => this.noTouching = false);

    ngOnDestroy() {
        this.cleanupTime.next();
        this.cleanupTime.complete();
        this.contextTopicSubject.complete();
    };
}
